import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiEdit2, FiTrash2, FiSettings, FiActivity,
  FiMessageSquare, FiZap, FiClock, FiTrendingUp, FiUsers,
  FiGlobe, FiMail, FiPhone, FiSlack, FiCopy, FiEye, FiMic, FiDatabase
} from 'react-icons/fi';
import { BsWhatsapp, BsRobot } from 'react-icons/bs';
import { BiBot } from 'react-icons/bi';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import AIAgentPanel from '../components/AIAgentPanel';
import { VoiceLabPanel } from './AssistantBuilder/components/VoiceTab/VoiceLabPanel';
import { ChatLabPanel } from './AssistantBuilder/components/ChatTab/ChatLabPanel';
import { KnowledgeLabPanel } from './AssistantBuilder/components/KnowledgeTab/KnowledgeLabPanel';

interface Assistant {
  id: string;
  name: string;
  description: string;
  model: string;
  status: 'active' | 'inactive' | 'training';
  channels: string[];
  messagesHandled: number;
  successRate: number;
  responseTime: number;
  color: string;
  createdAt: string;
  updatedAt?: string;
  instructions?: string;
  temperature?: number;
  maxTokens?: number;
}

const AssistantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'settings' | 'agents' | 'voice' | 'chat' | 'knowledge'>('overview');

  useEffect(() => {
    if (id) {
      fetchAssistant();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchAssistant = async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get(`/assistants/${id}`);
      setAssistant(response.data.assistant);
    } catch (error: any) {
      console.error('Failed to fetch assistant:', error);
      if (error.response?.status === 404) {
        toast.error('Assistant not found');
      } else {
        toast.error('Failed to load assistant details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this assistant?')) return;
    
    try {
      await api.delete(`/assistants/${id}`);
      toast.success('Assistant deleted successfully');
      navigate('/assistants');
    } catch (error) {
      toast.error('Failed to delete assistant');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await api.post(`/assistants/${id}/clone`);
      toast.success('Assistant duplicated successfully');
      navigate(`/assistants/${response.data.assistant.id}`);
    } catch (error) {
      toast.error('Failed to duplicate assistant');
    }
  };

  const channelIcons: { [key: string]: React.ReactNode } = {
    sms: <FiMessageSquare size={16} />,
    whatsapp: <BsWhatsapp size={16} />,
    webchat: <FiGlobe size={16} />,
    phone: <FiPhone size={16} />,
    email: <FiMail size={16} />,
    slack: <FiSlack size={16} />
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!assistant) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto text-center">
          <BiBot className="w-20 h-20 mx-auto text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Assistant Not Found</h2>
          <p className="text-gray-400 mb-6">The assistant you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/assistants')}
            className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Back to Assistants
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/assistants')}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            >
              <FiArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg bg-gradient-to-br ${assistant.color}`}>
                <BiBot size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{assistant.name}</h1>
                <p className="text-gray-400">{assistant.description}</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
              assistant.status === 'active' ? 'bg-green-500/20 text-green-400' :
              assistant.status === 'training' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {assistant.status}
            </div>
          </div>
          
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDuplicate}
              className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all flex items-center gap-2"
            >
              <FiCopy size={16} />
              Duplicate
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/assistants/${id}/edit`)}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-all flex items-center gap-2"
            >
              <FiEdit2 size={16} />
              Edit
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDelete}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all flex items-center gap-2"
            >
              <FiTrash2 size={16} />
              Delete
            </motion.button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: null },
            { id: 'voice', label: 'Voice Lab', icon: <FiMic size={16} /> },
            { id: 'chat', label: 'Chat Lab', icon: <FiMessageSquare size={16} /> },
            { id: 'knowledge', label: 'Knowledge Lab', icon: <FiDatabase size={16} /> },
            { id: 'performance', label: 'Performance', icon: null },
            { id: 'settings', label: 'Settings', icon: null },
            { id: 'agents', label: 'Agents', icon: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            {/* Stats Cards */}
            <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <FiMessageSquare className="text-blue-400" size={24} />
                <span className="text-xs text-gray-500">Total</span>
              </div>
              <h3 className="text-2xl font-bold text-white">{assistant.messagesHandled.toLocaleString()}</h3>
              <p className="text-sm text-gray-400">Messages Handled</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <FiTrendingUp className="text-green-400" size={24} />
                <span className="text-xs text-gray-500">Rate</span>
              </div>
              <h3 className="text-2xl font-bold text-white">{assistant.successRate}%</h3>
              <p className="text-sm text-gray-400">Success Rate</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <FiClock className="text-purple-400" size={24} />
                <span className="text-xs text-gray-500">Avg</span>
              </div>
              <h3 className="text-2xl font-bold text-white">{assistant.responseTime}s</h3>
              <p className="text-sm text-gray-400">Response Time</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <FiZap className="text-yellow-400" size={24} />
                <span className="text-xs text-gray-500">Active</span>
              </div>
              <h3 className="text-2xl font-bold text-white">{assistant.channels.length}</h3>
              <p className="text-sm text-gray-400">Channels</p>
            </div>
          </motion.div>
        )}

        {activeTab === 'performance' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50"
          >
            <h2 className="text-xl font-bold text-white mb-4">Performance Metrics</h2>
            <p className="text-gray-400">Performance analytics and metrics will be displayed here.</p>
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-6 border border-gray-700/50"
          >
            <h2 className="text-xl font-bold text-white mb-4">Configuration</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400">Model</label>
                <p className="text-white">{assistant.model}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Temperature</label>
                <p className="text-white">{assistant.temperature || 0.7}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Max Tokens</label>
                <p className="text-white">{assistant.maxTokens || 2048}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Active Channels</label>
                <div className="flex gap-2 mt-2">
                  {assistant.channels.map((channel) => (
                    <div key={channel} className="flex items-center gap-2 px-3 py-1 bg-gray-700 rounded-lg">
                      {channelIcons[channel]}
                      <span className="text-white capitalize">{channel}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'voice' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-[400px]"
          >
            <VoiceLabPanel
              assistantId={assistant.id}
              onVoiceChange={(voice) => {
                console.log('Voice changed:', voice);
              }}
            />
          </motion.div>
        )}

        {activeTab === 'chat' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-[400px]"
          >
            <ChatLabPanel
              assistant={assistant as any}
              globalPrompt={assistant.instructions}
              onMemoryUpdate={(hasMemory) => {
                console.log('Memory status:', hasMemory);
              }}
            />
          </motion.div>
        )}

        {activeTab === 'knowledge' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-[400px]"
          >
            <KnowledgeLabPanel
              assistantId={assistant.id}
              onDocumentsChange={(count) => {
                console.log('Document count:', count);
              }}
            />
          </motion.div>
        )}

        {activeTab === 'agents' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <AIAgentPanel assistantId={assistant.id} />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AssistantDetails;